import { Component, OnInit } from "@angular/core"
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms"
import { MatButtonModule } from "@angular/material/button"
import { MatCardModule } from "@angular/material/card"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { Router, RouterLink, RouterModule, ActivatedRoute } from "@angular/router"
import { SnackbarService } from "../../services/snackbar.service"
import { CognitoService } from "../../services/cognito.service"
import { AuthService } from "../../services/auth.service"
import { LoadingSpinnerComponent } from "../../shared/loading-spinner/loading-spinner.component"
import { AuthFloatLabelDirective } from "../../directives/auth-float-label.directive"

@Component({
  selector: "app-otp-verification",
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    RouterLink,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    AuthFloatLabelDirective,
  ],
  templateUrl: "./otp-verification.component.html",
  styleUrl: "./otp-verification.component.css",
})
export class OtpVerificationComponent implements OnInit {
  otpForm: FormGroup
  loading = false
  error = ""
  success = ""
  countdown = 0
  canResend = false
  email = ""
  verificationType: "signup" | "reset" = "signup"

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cognitoService: CognitoService,
    private authService: AuthService, // ✅ Add AuthService
    private snackbar: SnackbarService,
  ) {
    this.otpForm = this.formBuilder.group({
      otp: ["", [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    })
  }

  ngOnInit() {
    const registrationData = this.authService.getRegistrationData()

    this.route.queryParams.subscribe((params) => {
      this.email = registrationData?.email || params["email"] || ""
      this.verificationType = params["type"] || "signup"

      console.log("Registration data:", registrationData)
      console.log("Query params:", params)
      console.log("Using email:", this.email)
      console.log("Verification type:", this.verificationType)

      if (!this.email) {
        console.error("No email found for verification")
        this.snackbar.error("Registration data not found. Please try registering again.", 5000)
        this.router.navigate(["/signup"])
        return
      }
    })

    this.startCountdown()
  }

  onSubmit() {
    if (this.otpForm.invalid) return

    this.loading = true
    this.error = ""
    this.success = ""

    const otp = this.otpForm.get("otp")?.value

    console.log("Submitting OTP:", otp, "for email:", this.email, "type:", this.verificationType)

    if (this.verificationType === "signup") {
      this.cognitoService.confirmSignUp(this.email, otp).subscribe({
        next: (response: any) => {
          this.loading = false
          this.success = "Email verified successfully!"
          this.authService.clearRegistrationData()
          this.snackbar.success("Email verified! You can now log in.", 3000)
          setTimeout(() => this.router.navigate(["/login"]), 2000)
        },
        error: (error: any) => {
          this.loading = false
          this.error = error.error?.message || "Invalid verification code. Please try again."
          this.otpForm.get("otp")?.setValue("")
          this.snackbar.error(this.error, 5000)
        },
      })
    } else if (this.verificationType === "reset") {
      // For password reset, navigate to set password page with the code
      this.router.navigate(["/set-password"], {
        queryParams: {
          email: this.email,
          code: otp,
          type: "reset",
        },
      })
    }
  }

  resendOTP() {
    if (!this.canResend) return

    this.loading = true
    this.error = ""
    this.success = ""

    console.log("Resending OTP for email:", this.email, "type:", this.verificationType)

    if (this.verificationType === "reset") {
      // For password reset, resend forgot password code
      this.cognitoService.forgotPassword(this.email).subscribe({
        next: (response: any) => {
          this.loading = false
          this.success = "Verification code resent successfully!"
          this.snackbar.success("Code resent! Check your email.", 3000)
          this.startCountdown()
        },
        error: (error: any) => {
          this.loading = false
          this.error = error.error?.message || "Failed to resend code. Please try again."
          this.snackbar.error(this.error, 5000)
        },
      })
    } else {
      this.cognitoService.resendConfirmationCode(this.email).subscribe({
        next: (response: any) => {
          this.loading = false
          this.success = "Verification code resent successfully!"
          this.snackbar.success("Code resent! Check your email.", 3000)
          this.startCountdown()
        },
        error: (error: any) => {
          this.loading = false
          this.error = error.error?.message || "Failed to resend code. Please try again."
          this.snackbar.error(this.error, 5000)
        },
      })
    }
  }

  private startCountdown() {
    this.countdown = 60
    this.canResend = false

    const timer = setInterval(() => {
      this.countdown--
      if (this.countdown <= 0) {
        clearInterval(timer)
        this.canResend = true
      }
    }, 1000)
  }

  onOtpInput(event: any) {
    const input = event.target
    const value = input.value.replace(/\D/g, "")
    if (value.length > 6) {
      input.value = value.slice(0, 6)
    }
    this.otpForm.get("otp")?.setValue(input.value)
  }

  formatEmail(email: string): string {
    if (!email) return ""
    const [localPart, domain] = email.split("@")
    if (localPart.length <= 3) return email
    const maskedLocal = localPart.charAt(0) + "*".repeat(localPart.length - 2) + localPart.charAt(localPart.length - 1)
    return `${maskedLocal}@${domain}`
  }

  getVerificationMessage(): string {
    return this.verificationType === "signup"
      ? "We've sent a 6-digit verification code to"
      : "We've sent a 6-digit password reset code to"
  }

  getPageTitle(): string {
    return this.verificationType === "signup" ? "Verify Your Email" : "Verify Reset Code"
  }
}
